// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ZoomToBoundsThunk } from 'Features/map/mapCommonThunk';
import { toggleSelected, selectRange, deselectRange, setHovered, clearHovered, setFlash } from './fileSelectionSlice';
import { selectIsSelected, selectIsHoveredFromMap, selectIsTopHoveredFromMap } from './fileSelectionSelectors';
import scrollRowIntoPane from './scrollRowIntoPane';

// Shared wiring for a sidebar file row (2D viewer Orthomosaic + Project Details
// Data Sets): click toggles selection, hover syncs to the map, a map-originated
// hover scrolls the row into view, double-click zooms to the file's bounds.
// No click-vs-dblclick timer is needed: the dblclick's own two clicks toggle
// selection twice (net no-op) before the zoom fires.
export const useFileRowSelection = (
  kind: FileSelectionKind,
  id: string,
  bounds?: [number, number, number, number] | null,
  // The pane's row order; enables shift-click range selection when provided.
  orderedIds?: string[],
) => {
  const dispatch = useAppDispatch();
  const anchor = useAppSelector((state) => state.fileSelection.anchor[kind]);
  const selected = useAppSelector((state) => selectIsSelected(state, kind, id));
  const hoveredFromMap = useAppSelector((state) => selectIsHoveredFromMap(state, kind, id));
  const topHoveredFromMap = useAppSelector((state) => selectIsTopHoveredFromMap(state, kind, id));
  const hoverMuted = useAppSelector((state) => state.map.common.extentsHoverMuted);
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Pane-only scroll: scrollIntoView would also move the page scroller on
    // admin, pulling the map out from under the hovering cursor.
    if (topHoveredFromMap && rowRef.current) {
      scrollRowIntoPane(rowRef.current);
    }
  }, [topHoveredFromMap]);

  const onClick = (event?: { shiftKey?: boolean }) => {
    if (event?.shiftKey && anchor && orderedIds?.length) {
      const from = orderedIds.indexOf(anchor);
      const to = orderedIds.indexOf(id);
      if (from !== -1 && to !== -1) {
        const [start, end] = from < to ? [from, to] : [to, from];
        const ids = orderedIds.slice(start, end + 1);
        // The clicked row's own toggle direction applies to the run (D59).
        dispatch(selected ? deselectRange({ kind, ids }) : selectRange({ kind, ids }));
        return;
      }
    }
    dispatch(toggleSelected({ kind, id }));
  };
  // Click-only level: a row hover stops reaching the map, like map-side
  // hover; clicks, ranges and zoom stay live.
  const onMouseEnter = () => {
    if (!hoverMuted) dispatch(setHovered({ source: 'sidebar', files: [{ kind, id }] }));
  };
  const onMouseLeave = () => dispatch(clearHovered());
  const onDoubleClick = () => {
    if (!bounds) return;
    dispatch(setFlash({ kind, id, ts: Date.now() }));
    dispatch(ZoomToBoundsThunk(bounds));
  };

  return { selected, hoveredFromMap, rowRef, onClick, onMouseEnter, onMouseLeave, onDoubleClick };
};

export default useFileRowSelection;
